import { useLocation } from "react-router-dom";
import { HomeIcon, PhoneIcon, UserIcon } from "lucide-react";
import { IonContent, IonPage } from "@ionic/react";
import BackButton from "../../../components/common/BackButton";
import SecureImage from "../../../components/common/SecureImage";
import defaultImage from "../../../assets/images/bg_pattern.png";
import useUnhidePageOnEnter from "../../../hooks/useUnhidePageOnEnter";

export default function ServiceData() {
  // Ionic can leave this page `ion-page-hidden` on re-entry, blanking it.
  const pageRef = useUnhidePageOnEnter();
  const { state } = useLocation();
  const { service, provider } = state || {};

  return (
    <IonPage ref={pageRef}>
      <IonContent>
        <div className="navigo-booking-container-back-button flex items-center gap-2 fixed top-0 left-0 w-full bg-white z-50 px-4 py-2">
          <BackButton />
          <p className="font-semibold">
            {`Speciality: ${service?.name || provider?.name || "Details"}`}
          </p>
        </div>

        <div className="pt-16 p-4">
          <div className="border rounded-lg p-4 mb-4 shadow-md">
            <div className="flex items-start gap-4">
              <div className="w-24 h-24">
                {service?.imageMobile ? (
                  <img
                    src={service.imageMobile}
                    alt="Service"
                    className="w-full h-full object-cover rounded"
                  />
                ) : provider?.mobileCover ? (
                  <SecureImage
                    defaultImage={defaultImage}
                    src={provider.mobileCover}
                    className="w-full h-full object-cover rounded"
                  />
                ) : (
                  <img
                    src="/default.jpg"
                    alt="Default"
                    className="w-full h-full object-cover rounded"
                  />
                )}
              </div>

              <div>
                <p className="text-lg font-semibold">
                  {service?.name || provider?.name}
                </p>
                <p className="text-sm text-gray-600">
                  {service?.description || provider?.shortdescription}
                </p>
              </div>
            </div>
          </div>

          {service?.serviceModeDetails && (
            <>
              <h2 className="font-semibold text-base mb-2">
                Available consultation mode
              </h2>
              <div className="flex gap-2 flex-wrap mb-4">
                {service?.serviceModeDetails?.map((mode, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-violet-100 rounded"
                  >
                    {mode.modeName === "Tele consultation" && (
                      <PhoneIcon size={16} />
                    )}
                    {mode.modeName === "In person consultation" && (
                      <UserIcon size={16} />
                    )}
                    {mode.modeName === "Home sample" && <HomeIcon size={16} />}
                    {mode.modeName}
                  </span>
                ))}
              </div>
            </>
          )}

          <h2 className="font-semibold text-base mb-2">About</h2>
          <div
            className="text-sm text-gray-700 whitespace-pre-line"
            dangerouslySetInnerHTML={{
              __html:
                service?.descriptionLong ||
                provider?.longDescription ||
                "No detailed description available.",
            }}
          />

          {provider?.availability && (
            <>
              <h2 className="font-semibold text-base mt-4 mb-2">
                Availability
              </h2>
              <div className="flex flex-wrap gap-2">
                {provider.availability.map((day, index) => (
                  <div
                    key={index}
                    className={`w-10 h-12 rounded-md flex flex-col items-center justify-center text-sm font-medium ${
                      day.IsSlotAvailable
                        ? "bg-green-200 text-gray-800"
                        : "bg-gray-200 text-gray-400"
                    }`}
                  >
                    <span>{day.dayStr}</span>
                    <span>{day.dayNum}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          {provider?.languages && (
            <>
              <h2 className="font-semibold text-base mb-2">Languages known</h2>
              <div className="flex gap-2 flex-wrap mb-4">
                {provider?.languages?.map((lang, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-violet-100 rounded"
                  >
                    {lang}
                  </span>
                ))}
              </div>
            </>
          )}
          {provider?.address && (
            <>
              <h2 className="font-semibold text-base mb-2">Address</h2>
              <div className="text-sm text-gray-700 mb-4 whitespace-pre-line leading-6">
                {provider.address.housing && (
                  <div>{provider?.address?.housing}</div>
                )}
                {provider.address.area && <div>{provider?.address?.area}</div>}
                {provider.address.landmark && (
                  <div>Landmark: {provider?.address?.landmark}</div>
                )}
                {provider.address?.city && provider.address?.state && (
                  <div>
                    {provider?.address?.city}, {provider?.address?.state}
                  </div>
                )}
                {provider.address?.district && (
                  <div>District: {provider.address?.district}</div>
                )}
                {provider.address.pincode && (
                  <div>PIN: {provider.address?.pincode}</div>
                )}
              </div>
            </>
          )}
          {provider?.licenseNumber && (
            <div>
              <h2 className="font-semibold text-base mb-2">License number</h2>
              <div>{provider?.licenseNumber}</div>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
